import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronLeft, ChevronRight, X } from 'lucide-react';
import OpsLayout from '../components/OpsLayout';
import '../css/UserWithdrawManagement.css';
import { API_BASE_URL } from '../config/config';
import opsStatusTone from '../utils/opsStatusTone';

const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

const STATUS_TEXT_MAP = {
    'ACCEPTED': '요청 접수',
    'ADMIN_CANCEL': '관리자 취소',
    'ADMIN_FINISH': '관리자 종결',
    'USER_CANCEL': '사용자 취소',
    'AUTO_FINISH': '자동 종결'
};

const ACCOUNT_STATUS_TEXT_MAP = {
    'NORMAL': '정상',
    'WAIT_FOR_WITHDRAW': '탈퇴 대기',
    'WITHDRAWN': '탈퇴',
    'BLOCKED': '정지'
};

const getStatusText = (status) => STATUS_TEXT_MAP[status] || status;
const getAccountStatusText = (status) => ACCOUNT_STATUS_TEXT_MAP[status] || status;

// 세그먼트 값·상태 이름은 기존 <select> 의 것을 그대로 쓴다(statusFilter 는 'all' 로 시작한다 — '' 가 아니다).
const STATUS_FILTERS = [
    ['all', '전체'], ['ACCEPTED', '요청 접수'], ['ADMIN_CANCEL', '관리자 취소'],
    ['ADMIN_FINISH', '관리자 종결'], ['USER_CANCEL', '사용자 취소'], ['AUTO_FINISH', '자동 종결']
];

// "다른 상태로" 메뉴 항목. 두 상태(관리자 종결·관리자 취소)는 바로 누르는 버튼으로 남아 있다.
const MORE_STATUS_OPTIONS = [
    ['ACCEPTED', '요청 접수'], ['USER_CANCEL', '사용자 취소'], ['AUTO_FINISH', '자동 종결']
];

const WithdrawRequestModal = ({ request, onClose, onStatusUpdate }) => {
    const [isProcessing, setIsProcessing] = useState(false);

    if (!request) return null;

    const handleStatusUpdate = async (newStatus) => {
        if (!window.confirm(`정말로 이 요청을 ${newStatus === 'ADMIN_FINISH' ? '확정' : '취소'}하시겠습니까?`)) {
            return;
        }

        setIsProcessing(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`${API_BASE_URL}/api/user-withdraw/update-status`, {
                requestId: request.id,
                status: newStatus
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                window.alert('상태가 성공적으로 업데이트되었습니다.');
                onStatusUpdate();
                onClose();
            } else {
                window.alert('상태 업데이트에 실패했습니다: ' + response.data.message);
            }
        } catch (error) {
            console.error('상태 업데이트 오류:', error);
            window.alert('상태 업데이트 중 오류가 발생했습니다.');
        } finally {
            setIsProcessing(false);
        }
    };

    const statusLabel = getStatusText(request.status);
    const accountLabel = getAccountStatusText(request.user_status);

    return (
        <div className="sds-modal-overlay" onClick={onClose}>
            <div
                className="sds-modal"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="withdraw-modal-title"
            >
                <div className="sds-modal__head">
                    <h2 id="withdraw-modal-title" className="sds-modal__title">탈퇴 요청 상세</h2>
                    <button className="sds-btn sds-btn--ghost sds-btn--sm" onClick={onClose} aria-label="닫기">
                        <X size={16} aria-hidden="true" />
                    </button>
                </div>

                <div className="sds-modal__body">
                    <dl className="ops-page__kv">
                        <dt>요청 ID</dt><dd>{request.id}</dd>
                        <dt>사용자 ID</dt><dd>{request.user_id}</dd>
                        <dt>사용자명</dt><dd>{request.user_name}</dd>
                        <dt>연락처</dt><dd>{request.user_mobile}</dd>
                        <dt>요청 상태</dt>
                        <dd><span className={`sds-badge sds-badge--${opsStatusTone(statusLabel)}`}>{statusLabel}</span></dd>
                        <dt>계정 상태</dt>
                        <dd><span className={`sds-badge sds-badge--${opsStatusTone(accountLabel)}`}>{accountLabel}</span></dd>
                        <dt>요청 일시</dt><dd>{formatDate(request.created_at)}</dd>
                        <dt>처리 일시</dt><dd>{formatDate(request.updated_at)}</dd>
                    </dl>

                    {request.status === 'ACCEPTED' && (
                        <div className="withdraw-page__modal-actions">
                            <h3 className="withdraw-page__modal-actions-title">처리 옵션</h3>
                            <div className="withdraw-page__modal-actions-buttons">
                                <button
                                    className="sds-btn sds-btn--primary"
                                    onClick={() => handleStatusUpdate('ADMIN_FINISH')}
                                    disabled={isProcessing}
                                >
                                    탈퇴 확정
                                </button>
                                <button
                                    className="sds-btn sds-btn--weak"
                                    onClick={() => handleStatusUpdate('ADMIN_CANCEL')}
                                    disabled={isProcessing}
                                >
                                    탈퇴 취소
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// 조회 실패를 사용자가 다음에 무엇을 해야 하는지 알 수 있는 문구로 옮긴다.
// 상태 코드를 그대로 보여주면 "500" 만 남고 무엇을 해야 할지 알 수 없다.
const describeLoadError = (error, serverMessage) => {
    const status = error?.response?.status;
    const detail = serverMessage || error?.response?.data?.message;

    if (status === 401) return '세션이 만료되었습니다. 다시 로그인해 주세요.';
    if (status === 403) return '이 화면을 볼 권한이 없습니다. 최고관리자 계정으로 로그인해 주세요.';
    if (status >= 500) {
        return `서버에서 목록을 만들지 못했습니다. 잠시 후 다시 시도해 주세요.${detail ? ` (${detail})` : ''}`;
    }
    if (error && !error.response) return '서버에 연결하지 못했습니다. 네트워크 상태를 확인해 주세요.';
    return detail || '탈퇴 요청을 불러오지 못했습니다.';
};

export default function UserWithdrawManagement() {
    const [withdrawRequests, setWithdrawRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [itemsPerPage] = useState(10);
    const [selectedRequests, setSelectedRequests] = useState([]);
    const [bulkProcessing, setBulkProcessing] = useState(false);
    const [moreOpen, setMoreOpen] = useState(false);
    // 조회 실패 사유. 예전에는 오류를 삼키고 빈 목록만 남겨서, 권한이 없는 것인지
    // 서버가 죽은 것인지 정말 요청이 없는 것인지 화면만 봐서는 알 수 없었다
    // (2026-09-01 운영에서 500 이 "탈퇴 요청이 없습니다" 로 보였다).
    const [loadError, setLoadError] = useState(null);
    const moreMenuRef = useRef(null);

    // SUPER_ADMIN 권한 확인
    const isSuperAdmin = localStorage.getItem('is_super_admin') === 'true';

    useEffect(() => {
        if (!isSuperAdmin) {
            window.alert('접근 권한이 없습니다.');
            window.history.back();
            return;
        }

        fetchWithdrawRequests();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuperAdmin, currentPage, statusFilter]);

    // "다른 상태로" 메뉴: Escape 로 닫고, 메뉴 밖을 누르면 닫는다.
    useEffect(() => {
        if (!moreOpen) return undefined;

        const handleMouseDown = (e) => {
            if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) {
                setMoreOpen(false);
            }
        };
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') setMoreOpen(false);
        };

        document.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleMouseDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [moreOpen]);

    const fetchWithdrawRequests = async () => {
        try {
            setLoading(true);
            setLoadError(null);
            const token = localStorage.getItem('token');
            const params = {
                page: currentPage,
                limit: itemsPerPage,
                status: statusFilter !== 'all' ? statusFilter : undefined,
                search: searchTerm || undefined,
                is_super_admin: 'true',
                sortBy: 'created_at',
                sortOrder: 'desc'
            };

            const response = await axios.get(`${API_BASE_URL}/api/user-withdraw/requests`, {
                params: params,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                setWithdrawRequests(response.data.data.requests || []);
                setTotalPages(Math.ceil((response.data.data.total || 0) / itemsPerPage));
            } else {
                console.error('탈퇴 요청 조회 실패:', response.data.message);
                setWithdrawRequests([]);
                setLoadError(describeLoadError(null, response.data.message));
            }
        } catch (error) {
            console.error('탈퇴 요청 조회 오류:', error);
            setWithdrawRequests([]);
            setLoadError(describeLoadError(error));
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        setCurrentPage(1);
        fetchWithdrawRequests();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleSearch();
    };

    const handleStatusFilterSelect = (value) => {
        setStatusFilter(value);
        setCurrentPage(1);
    };

    const handleRequestClick = (request) => {
        setSelectedRequest(request);
    };

    const handleCloseModal = () => {
        setSelectedRequest(null);
    };

    const handleStatusUpdate = () => {
        fetchWithdrawRequests();
        setSelectedRequests([]); // 선택 초기화
    };

    // 체크박스 선택 처리
    const handleRequestSelect = (requestId) => {
        setSelectedRequests(prev => (
            prev.includes(requestId) ? prev.filter(id => id !== requestId) : [...prev, requestId]
        ));
    };

    // 전체 선택/해제
    const handleSelectAll = () => {
        const allRequestIds = withdrawRequests.map(request => request.id);
        if (selectedRequests.length === allRequestIds.length && allRequestIds.length > 0) {
            setSelectedRequests([]);
        } else {
            setSelectedRequests(allRequestIds);
        }
    };

    // 대량 처리
    const handleBulkUpdate = async (status) => {
        if (selectedRequests.length === 0) {
            alert('처리할 요청을 선택해주세요.');
            return;
        }

        const actionText = STATUS_TEXT_MAP[status] || status;
        if (!window.confirm(`선택된 ${selectedRequests.length}개의 요청을 "${actionText}" 상태로 변경하시겠습니까?`)) {
            return;
        }

        setBulkProcessing(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`${API_BASE_URL}/api/user-withdraw/bulk-update`, {
                requestIds: selectedRequests,
                status: status
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                alert(response.data.message);
                fetchWithdrawRequests();
                setSelectedRequests([]);
            } else {
                alert('대량 처리에 실패했습니다: ' + response.data.message);
            }
        } catch (error) {
            console.error('대량 처리 오류:', error);
            alert('대량 처리 중 오류가 발생했습니다.');
        } finally {
            setBulkProcessing(false);
        }
    };

    if (!isSuperAdmin) {
        return null;
    }

    const isAllSelected = withdrawRequests.length > 0 && selectedRequests.length === withdrawRequests.length;

    return (
        <OpsLayout title="사용자 탈퇴 관리" description="요청을 확인하고 탈퇴를 확정하거나 취소해요.">
            <section className="ops-page__card withdraw-page">
                <div className="sds-toolbar">
                    <input
                        className="sds-input withdraw-page__search"
                        placeholder="사용자명 으로 검색..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={handleKeyDown}
                    />
                    <button className="sds-btn sds-btn--secondary" onClick={handleSearch}>검색</button>
                    <span className="sds-toolbar__spacer" />
                    <div className="sds-seg" role="radiogroup" aria-label="상태 필터">
                        {STATUS_FILTERS.map(([value, label]) => (
                            <button
                                key={value}
                                role="radio"
                                aria-checked={statusFilter === value}
                                className={`sds-seg__item${statusFilter === value ? ' is-active' : ''}`}
                                onClick={() => handleStatusFilterSelect(value)}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>

                {selectedRequests.length > 0 && (
                    <div className="withdraw-page__bulk" role="region" aria-label="선택한 요청 일괄 처리">
                        <b>선택된 요청 {selectedRequests.length}개</b>
                        <span className="sds-toolbar__spacer" />
                        <button
                            className="sds-btn sds-btn--primary sds-btn--sm"
                            onClick={() => handleBulkUpdate('ADMIN_FINISH')}
                            disabled={bulkProcessing}
                        >
                            관리자 종결
                        </button>
                        <button
                            className="sds-btn sds-btn--weak sds-btn--sm"
                            onClick={() => handleBulkUpdate('ADMIN_CANCEL')}
                            disabled={bulkProcessing}
                        >
                            관리자 취소
                        </button>
                        <div className="withdraw-page__more" ref={moreMenuRef}>
                            <button
                                className="sds-btn sds-btn--ghost sds-btn--sm"
                                aria-haspopup="menu"
                                aria-expanded={moreOpen}
                                disabled={bulkProcessing}
                                onClick={() => setMoreOpen((v) => !v)}
                            >
                                다른 상태로<ChevronDown size={16} aria-hidden="true" />
                            </button>
                            {moreOpen && (
                                <div className="withdraw-page__menu" role="menu">
                                    {MORE_STATUS_OPTIONS.map(([code, label]) => (
                                        <button
                                            key={code}
                                            role="menuitem"
                                            className="withdraw-page__menu-item"
                                            onClick={() => { setMoreOpen(false); handleBulkUpdate(code); }}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="sds-notice">데이터를 불러오는 중...</div>
                ) : loadError ? (
                    /* 오류를 빈 목록으로 바꾸지 않는다. "요청이 없음"과 "불러오지 못함"은 다르다. */
                    <div className="sds-notice sds-notice--error withdraw-page__error">
                        <div>
                            <div className="withdraw-page__error-title">목록을 불러오지 못했습니다</div>
                            <div>{loadError}</div>
                            <button className="sds-btn sds-btn--secondary sds-btn--sm withdraw-page__retry" onClick={fetchWithdrawRequests}>
                                다시 시도
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="sds-table-wrap">
                        <table className="sds-table">
                            <thead>
                                <tr>
                                    <th className="sds-table__center">
                                        <input
                                            type="checkbox"
                                            checked={isAllSelected}
                                            onChange={handleSelectAll}
                                            disabled={bulkProcessing}
                                            aria-label="전체 선택"
                                        />
                                    </th>
                                    <th>요청 ID</th>
                                    <th>사용자</th>
                                    <th>연락처</th>
                                    <th>요청 상태</th>
                                    <th>계정 상태</th>
                                    <th>요청 일시</th>
                                    <th>처리 일시</th>
                                </tr>
                            </thead>
                            <tbody>
                                {withdrawRequests.length > 0 ? withdrawRequests.map((request) => {
                                    const checked = selectedRequests.includes(request.id);
                                    const statusLabel = getStatusText(request.status);
                                    const accountLabel = getAccountStatusText(request.user_status);
                                    return (
                                        <tr
                                            key={request.id}
                                            className={`ops-page__row--clickable${checked ? ' is-selected' : ''}`}
                                            onClick={(e) => {
                                                // 체크박스 클릭 시에는 행 클릭 이벤트 방지
                                                if (e.target.type === 'checkbox') return;
                                                handleRequestClick(request);
                                            }}
                                        >
                                            <td className="sds-table__center">
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => handleRequestSelect(request.id)}
                                                    disabled={bulkProcessing}
                                                    aria-label={`${request.user_name || request.id} 선택`}
                                                />
                                            </td>
                                            <td>{request.id}</td>
                                            <td>
                                                <b>{request.user_name}</b>
                                                <small className="withdraw-page__sub">ID {request.user_id}</small>
                                            </td>
                                            <td>{request.user_mobile}</td>
                                            <td>
                                                <span className={`sds-badge sds-badge--${opsStatusTone(statusLabel)}`}>{statusLabel}</span>
                                            </td>
                                            <td>
                                                <span className={`sds-badge sds-badge--${opsStatusTone(accountLabel)}`}>{accountLabel}</span>
                                            </td>
                                            <td>{formatDate(request.created_at)}</td>
                                            <td>{formatDate(request.updated_at)}</td>
                                        </tr>
                                    );
                                }) : (
                                    <tr>
                                        <td colSpan={8} className="sds-empty">탈퇴 요청이 없습니다.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="sds-pagination">
                    <button
                        className="sds-pagination__btn"
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        aria-label="이전 페이지"
                    >
                        <ChevronLeft size={16} aria-hidden="true" />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                            key={page}
                            className={`sds-pagination__btn${currentPage === page ? ' is-active' : ''}`}
                            aria-current={currentPage === page ? 'page' : undefined}
                            onClick={() => setCurrentPage(page)}
                        >
                            {page}
                        </button>
                    ))}
                    <button
                        className="sds-pagination__btn"
                        onClick={() => setCurrentPage((p) => Math.max(1, Math.min(totalPages, p + 1)))}
                        disabled={currentPage >= totalPages}
                        aria-label="다음 페이지"
                    >
                        <ChevronRight size={16} aria-hidden="true" />
                    </button>
                </div>
            </section>

            {selectedRequest && (
                <WithdrawRequestModal
                    request={selectedRequest}
                    onClose={handleCloseModal}
                    onStatusUpdate={handleStatusUpdate}
                />
            )}
        </OpsLayout>
    );
}
